import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Device, type DeviceDecision, type DeviceStatus } from '../api/client';
import { AvisoError, AvisoExito } from '../components/Aviso';
import { BarraDeFiltros, Buscador, Segmentado } from '../components/Filtros';
import { Modal } from '../components/Modal';
import { AccionIcono } from '../components/AccionIcono';
import { IconoAprobar, IconoRechazar, IconoRetirar } from '../components/Iconos';

type Vista = DeviceStatus | 'ALL';
const VISTAS: Vista[] = ['PENDING', 'APPROVED', 'REJECTED', 'REVOKED', 'EXEMPT', 'ALL'];

const TONO: Record<DeviceStatus, string> = {
  PENDING: 'aviso',
  APPROVED: 'bueno',
  REJECTED: 'critico',
  REVOKED: 'critico',
  EXEMPT: 'neutro',
};

/** El contador del menú escucha esto para no esperar a su siguiente vuelta. */
export const CAMBIO_DE_DISPOSITIVOS = 'dispositivos:cambio';

/**
 * Dispositivos autorizados (ADR-0026). Lo primero que se ve es lo pendiente,
 * del más antiguo al más nuevo, con aprobar y rechazar a un clic y la opción de
 * aprobar varios a la vez: el día que esto sale, todos piden aprobación.
 *
 * Cada decisión le llega a la persona por correo en ese momento y queda en la
 * bitácora; si el correo falla, la decisión se queda y aquí se dice.
 */
export function Dispositivos() {
  const { t, i18n } = useTranslation();
  const [vista, setVista] = useState<Vista>('PENDING');
  const [dispositivos, setDispositivos] = useState<Device[]>([]);
  const [pendientes, setPendientes] = useState(0);
  const [busca, setBusca] = useState('');
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [avance, setAvance] = useState<{ actual: number; total: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [retirando, setRetirando] = useState<Device | null>(null);
  const fecha = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }),
    [i18n.language],
  );

  const cargar = useCallback(async () => {
    try {
      const [lista, cuenta] = await Promise.all([
        api.devices(vista === 'ALL' ? undefined : vista),
        api.devicePendingCount(),
      ]);
      setDispositivos(lista);
      setPendientes(cuenta.pending);
      setElegidos(new Set());
    } catch (e) {
      setError(e);
    }
  }, [vista]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const visibles = dispositivos.filter((d) => {
    const texto = `${d.fullName} ${d.username}`.toLowerCase();
    return !busca || texto.includes(busca.toLowerCase());
  });

  const nombre = (d: { kind: Device['kind']; browser: string; os: string }) =>
    t('devices.name', { kind: t(`devices.kind.${d.kind}`), browser: d.browser, os: d.os });

  const contar = (resultado: DeviceDecision, persona: string) =>
    `${t(`devices.decided.${resultado.status}`, { person: persona, device: resultado.device })} ${t(
      resultado.notified ? 'devices.notified' : 'devices.notNotified',
    )}`;

  async function decidir(d: Device, decision: 'approve' | 'reject' | 'revoke') {
    setOcupado(d.id);
    setError(null);
    setMensaje(null);
    try {
      const resultado = await api.decideDevice(d.id, decision);
      setMensaje(contar(resultado, d.fullName));
      window.dispatchEvent(new Event(CAMBIO_DE_DISPOSITIVOS));
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(null);
      setRetirando(null);
    }
  }

  /**
   * Uno por uno y no de golpe: cada aprobación es una decisión con su renglón en
   * la bitácora y su correo, y si una falla las demás siguen.
   */
  async function aprobarElegidos() {
    const lista = visibles.filter((d) => elegidos.has(d.id));
    let aprobados = 0;
    let avisados = 0;
    setError(null);
    setMensaje(null);
    for (const [indice, d] of lista.entries()) {
      setAvance({ actual: indice + 1, total: lista.length });
      try {
        const resultado = await api.decideDevice(d.id, 'approve');
        aprobados += 1;
        if (resultado.notified) avisados += 1;
      } catch (e) {
        setError(e);
      }
    }
    setAvance(null);
    setMensaje(t('devices.bulkDone', { count: aprobados, notified: avisados }));
    window.dispatchEvent(new Event(CAMBIO_DE_DISPOSITIVOS));
    await cargar();
  }

  const pendientesVisibles = visibles.filter((d) => d.status === 'PENDING');
  const todosElegidos = pendientesVisibles.length > 0 && pendientesVisibles.every((d) => elegidos.has(d.id));
  const enVistaPendiente = vista === 'PENDING';

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('devices.title')}</h1>
        <p className="pagina__bajada">{t('devices.subtitle')}</p>
      </div>

      <AvisoError error={error} />
      {mensaje && <AvisoExito>{mensaje}</AvisoExito>}

      <section className="tarjeta">
        <BarraDeFiltros>
          <Segmentado
            etiqueta={t('devices.filterStatus')}
            valor={vista}
            onCambio={(valor) => setVista(valor as Vista)}
            opciones={VISTAS.map((uno) => ({
              valor: uno,
              etiqueta: t(`devices.tabs.${uno}`),
              ...(uno === 'PENDING' ? { conteo: pendientes } : {}),
            }))}
          />
          <Buscador etiqueta={t('devices.search')} valor={busca} onCambio={setBusca} />
        </BarraDeFiltros>

        {enVistaPendiente && pendientesVisibles.length > 0 && (
          <div className="tarjeta__acciones" style={{ marginBottom: '.8rem' }}>
            <button
              className="boton"
              type="button"
              disabled={elegidos.size === 0 || avance !== null}
              onClick={() => void aprobarElegidos()}
            >
              {avance
                ? t('devices.approving', { actual: avance.actual, total: avance.total })
                : t('devices.approveSelected', { count: elegidos.size })}
            </button>
          </div>
        )}

        {visibles.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">
              {t(enVistaPendiente && !busca ? 'devices.emptyPending' : 'devices.empty')}
            </span>
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  {enVistaPendiente && (
                    <th>
                      <input
                        type="checkbox"
                        aria-label={t('devices.selectAll')}
                        checked={todosElegidos}
                        onChange={(e) =>
                          setElegidos(e.target.checked ? new Set(pendientesVisibles.map((d) => d.id)) : new Set())
                        }
                      />
                    </th>
                  )}
                  <th>{t('devices.person')}</th>
                  <th>{t('devices.device')}</th>
                  <th>{t('devices.address')}</th>
                  <th>{t('devices.requested')}</th>
                  <th>{t('devices.status')}</th>
                  <th>{t('devices.decidedBy')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibles.map((d) => (
                  <tr key={d.id}>
                    {enVistaPendiente && (
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('devices.select', { person: d.fullName })}
                          checked={elegidos.has(d.id)}
                          onChange={(e) => {
                            const siguiente = new Set(elegidos);
                            if (e.target.checked) siguiente.add(d.id);
                            else siguiente.delete(d.id);
                            setElegidos(siguiente);
                          }}
                        />
                      </td>
                    )}
                    <td>
                      {d.fullName}
                      <div className="campo__ayuda mono">{d.username}</div>
                    </td>
                    <td>
                      {t(`devices.kind.${d.kind}`)}
                      <div className="campo__ayuda">
                        {d.browser} · {d.os}
                      </div>
                    </td>
                    <td className="mono">{d.lastIp ?? d.firstIp ?? '—'}</td>
                    <td className="mono">
                      {fecha.format(new Date(d.requestedAt))}
                      {d.lastSeenAt && d.status !== 'APPROVED' && d.status !== 'EXEMPT' && (
                        <div className="campo__ayuda">
                          {t('devices.lastAttempt', { when: fecha.format(new Date(d.lastSeenAt)) })}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`pildora pildora--${TONO[d.status]}`}>
                        <span className="pildora__punto" aria-hidden="true" />
                        {t(`devices.statusName.${d.status}`)}
                      </span>
                    </td>
                    <td>
                      {d.decidedByUsername ? (
                        <>
                          <span className="mono">{d.decidedByUsername}</span>
                          {d.decidedAt && <div className="campo__ayuda">{fecha.format(new Date(d.decidedAt))}</div>}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="acciones-fila">
                      {d.status === 'PENDING' && (
                        <>
                          <AccionIcono
                            etiqueta={t('devices.approve')}
                            icono={<IconoAprobar />}
                            tono="principal"
                            disabled={ocupado === d.id || avance !== null}
                            onClick={() => void decidir(d, 'approve')}
                          />
                          {/* Sin confirmación: un rechazo se corrige aprobándolo después. */}
                          <AccionIcono
                            etiqueta={t('devices.reject')}
                            icono={<IconoRechazar />}
                            tono="peligro"
                            disabled={ocupado === d.id || avance !== null}
                            onClick={() => void decidir(d, 'reject')}
                          />
                        </>
                      )}
                      {(d.status === 'REJECTED' || d.status === 'REVOKED') && (
                        <AccionIcono
                          etiqueta={t('devices.approve')}
                          icono={<IconoAprobar />}
                          disabled={ocupado === d.id}
                          onClick={() => void decidir(d, 'approve')}
                        />
                      )}
                      {d.status === 'APPROVED' && (
                        <AccionIcono
                          etiqueta={t('devices.revoke')}
                          icono={<IconoRetirar />}
                          tono="peligro"
                          disabled={ocupado === d.id}
                          onClick={() => setRetirando(d)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="tarjeta__pie">{t('devices.note')}</p>
      </section>

      {/* Retirar sí se confirma: deja fuera a alguien que hoy entra. */}
      {retirando && (
        <Modal titulo={t('devices.revokeConfirm')} onCerrar={() => setRetirando(null)}>
          <p>{t('devices.revokeConfirmHint', { person: retirando.fullName, device: nombre(retirando) })}</p>
          <div className="modal__acciones">
            <button className="boton boton--fantasma" type="button" onClick={() => setRetirando(null)}>
              {t('groups.cancel')}
            </button>
            <button
              className="boton"
              type="button"
              disabled={ocupado === retirando.id}
              onClick={() => void decidir(retirando, 'revoke')}
            >
              {t('devices.revoke')}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
